import React, { FC, useEffect } from 'react'
import { Box, TextField, Select, MenuItem, Typography, Button, Stack, CircularProgress } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { fetchSites } from '@/features/sites/sitesThunks'
import { fetchManagers } from '@/features/options/optionsThunks'
import { selectManagers, selectOptionsLoading } from '@/features/options/optionsSelectors'

interface EventsFormFieldsProps {
  title: string
  place: string
  manager: string
  site: string
  date: Dayjs | null
  dateError: boolean
  editingId: string | null
  isSubmitting: boolean
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onPlaceChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onManagerChange: (e: any) => void
  onSiteChange: (e: any) => void
  onDateChange: (d: Dayjs | null) => void
  onReset: () => void
}

const EventsFormFields: FC<EventsFormFieldsProps> = ({
  title,
  place,
  manager,
  site,
  date,
  dateError,
  editingId,
  isSubmitting,
  onTitleChange,
  onPlaceChange,
  onManagerChange,
  onSiteChange,
  onDateChange,
  onReset,
}) => {
  const dispatch = useAppDispatch()
  const { sites, loading: sitesLoading } = useAppSelector((state) => state.sites)
  const managers = useAppSelector(selectManagers)
  const managersLoading = useAppSelector(selectOptionsLoading)

  // Fetch sites and managers on component mount
  useEffect(() => {
    if (sites.length === 0) {
      dispatch(fetchSites())
    }
    if (managers.length === 0) {
      dispatch(fetchManagers())
    }
  }, [dispatch, sites.length, managers.length])

  // Set default manager when managers are loaded and no manager is selected
  useEffect(() => {
    if (managers.length > 0 && !manager) {
      onManagerChange({ target: { value: managers[0] } })
    }
  }, [managers, manager, onManagerChange])

  return (
    <Stack spacing={4}>
      {/* Primera Fila: Titulo, Manager, Sede */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.5fr 1.5fr 1.5fr' },
          gap: 3,
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Titulo <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          <TextField
            size="medium"
            value={title}
            onChange={onTitleChange}
            placeholder="Título del evento"
            fullWidth
            required
            onInvalid={(e) => {
              (e.target as HTMLInputElement).setCustomValidity('Por favor completa este campo')
            }}
            onInput={(e) => {
              (e.target as HTMLInputElement).setCustomValidity('')
            }}
          />
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Responsable <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          <Select
            value={manager}
            onChange={onManagerChange}
            fullWidth
            size="medium"
            required
            disabled={managersLoading || managers.length === 0}
          >
            {managersLoading ? (
              <MenuItem disabled>
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              managers.map((managerItem) => (
                <MenuItem key={managerItem} value={managerItem}>
                  {managerItem}
                </MenuItem>
              ))
            )}
          </Select>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Sede <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          <Select
            value={site}
            onChange={onSiteChange}
            fullWidth
            size="medium"
            required
            disabled={sitesLoading || sites.length === 0}
          >
            {sitesLoading ? (
              <MenuItem disabled>
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              sites.map((item, index) => (
                <MenuItem key={item.id || index} value={item.Name}>
                  {item.Name}
                </MenuItem>
              ))
            )}
          </Select>
        </Box>
      </Box>

      {/* Segunda Fila: Fecha y Lugar */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.5fr 1.5fr 1.5fr' },
          gap: 3,
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Fecha y Hora <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <DateTimePicker
              value={date && dayjs.isDayjs(date) ? date : null}
              onChange={onDateChange}
              minDateTime={dayjs()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'medium',
                  error: dateError,
                  sx: {
                    ...(dateError && {
                      '& fieldset': {
                        borderColor: '#d32f2f !important',
                        borderWidth: '2px',
                      },
                    }),
                  },
                },
              }}
              format="DD-MM-YYYY HH:mm"
              ampm={true}
            />
          </LocalizationProvider>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Lugar <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          <TextField
            size="medium"
            value={place}
            onChange={onPlaceChange}
            placeholder="Auditorio - Sede Principal"
            fullWidth
            required
            onInvalid={(e) => {
              (e.target as HTMLInputElement).setCustomValidity('Por favor completa este campo')
            }}
            onInput={(e) => {
              (e.target as HTMLInputElement).setCustomValidity('')
            }}
          />
        </Box>
      </Box>

      {/* Botones */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 6, gap: 4 }}>
        {editingId && (
          <Button
            variant="outlined"
            onClick={onReset}
            disabled={isSubmitting}
            sx={{
              textTransform: 'none',
              px: 4,
              py: 1.2,
              fontSize: '14px',
              color: '#6b7280',
              borderColor: '#d1d5db',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f9fafb',
              },
            }}
          >
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{
            backgroundColor: '#1e3a5f',
            textTransform: 'none',
            px: 6,
            py: 1.2,
            fontSize: '14px',
            '&:hover': {
              backgroundColor: '#152a45',
            },
          }}
        >
          {editingId ? 'Actualizar' : 'Agregar'}
        </Button>
      </Box>
    </Stack>
  )
}

export default EventsFormFields
